import type { VoxelField } from "@/core/field/VoxelField";

/**
 * Ambient occlusion sampled straight from the distance field.
 *
 * Concave and convex are the two things a hold designer has to read at a
 * glance, and flat studio lighting hides both. Because we already hold a
 * signed distance field, occlusion is nearly free: step along the surface
 * normal and compare how far the field says the surface is with how far we
 * have walked. Where the field falls short, something is in the way.
 *
 * The result is written into the mesh's vertex colours, which
 * MeshStandardMaterial already multiplies into the diffuse term — no extra
 * shader, no post-processing pass and no extra dependency.
 *
 * Occlusion is computed once per INDEXED vertex and then expanded across the
 * triangle corners that share it. Doing it on the expanded, non-indexed
 * positions instead would repeat the same work about six times over, which is
 * the difference between a stroke feeling immediate and feeling sticky.
 */

const TAPS = 4;
const OFFSET: [number, number, number] = [0, 1, 0];

/** One occlusion value per vertex in `positions`. */
export function computeFieldAO(
  field: VoxelField,
  positions: Float32Array,
  strength = 0.42,
): Float32Array {
  const count = positions.length / 3;
  const ao = new Float32Array(count);
  const reach = field.h * 5;
  const normal: [number, number, number] = [...OFFSET];

  for (let v = 0; v < count; v++) {
    const px = positions[v * 3] as number;
    const py = positions[v * 3 + 1] as number;
    const pz = positions[v * 3 + 2] as number;
    field.gradient(px, py, pz, normal);

    let occlusion = 0;
    let scale = 1;
    for (let i = 1; i <= TAPS; i++) {
      const step = (reach * i) / TAPS;
      const d = field.sample(
        px + normal[0] * step,
        py + normal[1] * step,
        pz + normal[2] * step,
      );
      occlusion += (step - d) * scale;
      scale *= 0.72;
    }
    let value = 1 - (strength * 1.6 * occlusion) / reach;
    ao[v] = value < 0.28 ? 0.28 : value > 1 ? 1 : value;
  }
  return ao;
}

/**
 * Expand per-vertex occlusion into the flat vertex-colour buffer that matches
 * a non-indexed mesh built from `indices`.
 */
export function expandAOToColors(
  ao: Float32Array,
  indices: Uint32Array,
  triangleCount: number,
): Float32Array {
  const colors = new Float32Array(triangleCount * 9);
  for (let t = 0; t < triangleCount; t++) {
    for (let corner = 0; corner < 3; corner++) {
      const value = ao[indices[t * 3 + corner] as number] as number;
      const o = t * 9 + corner * 3;
      colors[o] = value;
      colors[o + 1] = value;
      colors[o + 2] = value;
    }
  }
  return colors;
}
